import { useEffect, useRef, useState } from 'react'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { api } from '@/store/api'
import { selectAuthToken } from '@/features/auth/selectors'
import { loseJob } from './jobsSlice'
import { useGetStudioJobQuery } from './studioApi'
import { BLOCK_WRITING_JOB_TYPES, isTerminalStatus } from './studioTypes'
import { useTranslationChain } from './useTranslationChain'

const POLL_MS = 3000

/**
 * Подписка на ОДНУ джобу. Хуки нельзя звать в цикле, поэтому поллер рендерит
 * по такому потомку на каждую запись реестра.
 *
 * Остановка — `pollingInterval: 0`, а НЕ `skip`: `skip` снимает подписку и
 * уносит с собой терминальный ответ (summary, credits_charged, skipped), ради
 * которого поллинг и затевался.
 *
 * Клиентского таймаута нет намеренно: джоба честно стоит в `running` до 10
 * минут после смерти, её закрывает жнец на бэке. Сетевая ошибка тоже не
 * останавливает опрос — следующий тик через 3 секунды.
 */
const JobSubscription = ({ courseId, jobId }: { courseId: number; jobId: number }) => {
  const dispatch = useAppDispatch()
  // Опции хука не могут читать его собственную `data` (переменная ещё не
  // объявлена), поэтому терминальность держим отдельным флагом: на рендере
  // после терминального ответа интервал становится 0 и опрос прекращается.
  const [stopped, setStopped] = useState(false)
  const { data, error, fulfilledTimeStamp, refetch } = useGetStudioJobQuery(jobId, {
    pollingInterval: stopped ? 0 : POLL_MS,
  })

  // §7.4: цепочка «перевод → картинки». `chainRequest` запоминает штамп
  // ОТВЕТА, который уже был на экране в момент, когда попросили перечитать
  // джобу («baseline») — «подтверждено» значит, что `fulfilledTimeStamp`
  // сдвинулся дальше этого штампа, то есть с момента запроса случился ХОТЯ
  // БЫ ОДИН новый успешный ответ — не обязательно именно от этого `refetch()`:
  // эффект ниже намеренно НЕ глушит штатный опрос той же джобы, пока перевод
  // не подтверждён, и фоновый тик, догнавший или подменивший упавший
  // `refetch()`, тоже полноправно подтверждает чтение (M-2, ревью раунд 1).
  // `asked` обязателен отдельно от `baseline !== undefined`: у уже
  // завершённого прогона `fulfilledTimeStamp` — реальное число ещё ДО того,
  // как мы вообще попросили перечитать, и без флага «confirmed» стало бы
  // `true` немедленно, вообще не дождавшись НИ ОДНОГО повторного чтения.
  const chainRequest = useRef<{ asked: boolean; baseline?: number }>({ asked: false })
  const confirmed =
    chainRequest.current.asked && fulfilledTimeStamp !== chainRequest.current.baseline
  useTranslationChain(
    courseId,
    data,
    () => {
      chainRequest.current = { asked: true, baseline: fulfilledTimeStamp }
      void refetch()
    },
    confirmed,
  )

  // Резерв заводится СИНХРОННО со стартом, а запись реестра появляется уже
  // после `201` — значит к монтированию подписки `held` на сервере уже стоит,
  // а в кеше лежит до-прогонный баланс. Без этого чтения заморозка не видна
  // НИКОГДА: следующая инвалидация баланса — только на терминальном статусе,
  // где резерв уже закрыт. Ровно один запрос на прогон (перецепление цепочки
  // монтирует новую подписку — там резерв тоже сменился), а не по тику: `held`
  // меняется только на старте и на расчёте.
  useEffect(() => {
    dispatch(api.util.invalidateTags(['CreditsBalance']))
  }, [jobId, dispatch])

  // А (Task 10, §6.2): пока прогон, ПИШУЩИЙ в блоки, ещё не терминален — кеш
  // блоков обновляется на КАЖДЫЙ успешный тик, а не только в конце. Иначе
  // реордер и сохранение блока во время прогона используют устаревшее тело
  // (`PUT` блока — полная замена) и стирают то, что бэк только что дописал, а
  // плашка конфликта (`useBlockDraft`) не успевает появиться раньше клика
  // «Сохранить». Список типов — ЕДИНСТВЕННЫЙ, `BLOCK_WRITING_JOB_TYPES`.
  //
  // Дублирует терминальный эффект ниже НАМЕРЕННО — тот шире (курс, смета,
  // баланс) и разово, на конец; этот — только `Block`, на каждый тик, пока
  // статус НЕ терминален. Зависимость — `fulfilledTimeStamp`, а не `data`:
  // structural sharing RTK Query держит ту же ссылку `data`, если ответ тика
  // содержимо не изменился, а тик всё равно случился.
  useEffect(() => {
    if (!data || isTerminalStatus(data.status) || !BLOCK_WRITING_JOB_TYPES.has(data.type)) return
    dispatch(api.util.invalidateTags([{ type: 'Block' }]))
  }, [fulfilledTimeStamp, data, dispatch])

  useEffect(() => {
    if (!isTerminalStatus(data?.status)) return
    // Джоба сама контента не несёт (§ контракта «после success ПЕРЕЧИТАТЬ»).
    // Инвалидация широкая и осознанная: успешный прогон меняет содержимое
    // курса целиком, а `failed` тоже инвалидируем — частичный результат мог
    // записаться. Course — по id (мы его знаем), Lesson/Block — без id
    // (RTK Query матчит ВСЕ записи этого типа независимо от их id, а точных
    // id уроков/блоков поллер не видит). Quote и CreditsBalance протухают
    // вместе: смета несёт `active_job`/`pending_units`, баланс — списание.
    // Это НЕ зависит от подтверждения цепочки (ниже) — контент курса менялся
    // независимо от того, придёт ли следом джоба картинок.
    dispatch(
      api.util.invalidateTags([
        { type: 'Course', id: courseId },
        // Списки «Мои курсы» тоже: с фазы 4 карточка несёт языки курса, а
        // прогон перевода заводит строку языка — без этого тега список до
        // истечения кеша показывал бы приглушённый чип «язык не объявлен»
        // курсу, которому язык только что объявили.
        { type: 'Course', id: 'LIST' },
        { type: 'Lesson' },
        { type: 'Block' },
        { type: 'Quote', id: courseId },
        'CreditsBalance',
        'CreditsHistory',
      ]),
    )
  }, [data?.status, courseId, dispatch])

  useEffect(() => {
    if (!isTerminalStatus(data?.status)) return
    // §7.4 (M-2, ревью раунд 1): пока это УСПЕШНЫЙ перевод, для которого
    // повторное чтение ещё не подтверждено, — НЕ глушим опрос. Следующий
    // штатный тик (через POLL_MS) бьёт по тому же `ai/jobs/{id}` и сам
    // подтвердит чтение — это и есть ретрай на случай, если явный `refetch()`
    // из `useTranslationChain` не долетел (сеть моргнула): без этой отсрочки
    // единственный сетевой сбой навсегда обрывал бы цепочку молча. Для
    // `failed`-перевода (цепочки не бывает — `isDoneTranslation` требует
    // `success`) и для всех остальных типов джоб `confirmed` тут ни при чём
    // — они гасят опрос как прежде, немедленно.
    const chainPending =
      data?.type === 'course_translation' && data.status === 'success' && !confirmed
    if (chainPending) return
    setStopped(true)
  }, [data?.status, data?.type, confirmed])

  const status = (error as { status?: number } | undefined)?.status
  useEffect(() => {
    // 404 job_not_found: сохранённый id выбросить. 403 job_not_author: джоба
    // не наша — держать её в реестре незачем. Не `detachJob`: вместе с записью
    // инвариант `chatSlice` уносит и историю чата, поэтому оставляем отметку,
    // по которой панель объяснит исчезновение ленты (M-6). Тостом это
    // показывать нельзя — эндпоинт в SILENT_ENDPOINTS намеренно.
    if (status === 404 || status === 403) dispatch(loseJob(courseId))
  }, [status, courseId, dispatch])

  return null
}

/** Глобальный поллер: монтируется один раз, живёт выше роутов, поэтому уход
 *  из редактора прогон не обрывает.
 *
 * Реестр — в persist-whitelist и переживает логаут (это отдельное решение,
 * реестр при логауте не чистится), поэтому без токена подписки вовсе не
 * рендерим: иначе незалогиненная вкладка бесконечно опрашивала бы
 * `ai/jobs/{id}` раз в 3 секунды, получая гарантированный 401 — см.
 * комментарий в `useMe.ts` про тот же класс проблемы. Это не тот `skip`,
 * который запрещает спека (спека запрещает снимать подписку у ЖИВОЙ джобы) —
 * здесь речь о том, чтобы не начинать её вовсе. */
export const JobPoller = () => {
  const byCourse = useAppSelector((s) => s.aiJobs.byCourse)
  // Boolean-селектор: ротации токена не должны перерендеривать поллер лишний раз.
  const hasToken = useAppSelector((s) => Boolean(selectAuthToken(s)))
  if (!hasToken) return null
  return (
    <>
      {Object.entries(byCourse).map(([courseId, entry]) => (
        <JobSubscription
          key={`${courseId}:${entry.jobId}`}
          courseId={Number(courseId)}
          jobId={entry.jobId}
        />
      ))}
    </>
  )
}
